import React, { useState } from 'react';

// Buyer deal details, grouped into sections. Each section has its own Edit button; every field in it can be changed.
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const money = (v) => (v ? `$${Number(String(v).replace(/[^0-9.]/g, '')).toLocaleString()}` : '');

// Reads a field. Keys like "lender.firstName" read inside an object.
const getVal = (tx, key) => {
  if (!key.includes('.')) return tx[key];
  const [obj, sub] = key.split('.');
  return (tx[obj] || {})[sub];
};

const yesNo = [['Yes', 'Yes'], ['No', 'No']];

const buildSections = (tx) => {
  const nc = tx.propertyStatus === 'new-construction';
  const financing = tx.paymentType === 'Financing';
  const homeSale = tx.homeSaleContingency === 'Yes';
  return [
    {
      title: 'Client',
      fields: [
        { key: 'firstName', label: 'First name' },
        { key: 'lastName', label: 'Last name' },
        { key: 'email', label: 'Email', type: 'email' },
        { key: 'phone', label: 'Phone', type: 'tel' },
        { key: 'leadSource', label: 'Lead generated by', type: 'select', options: [['agent-generated', 'Agent'], ['team-generated', 'Team']] },
        { key: 'source', label: 'Lead source', type: 'text' },
        { key: 'sourceOther', label: 'Lead source (other)', type: 'text', when: () => tx.source === 'Other' },
      ],
    },
    {
      title: 'Property',
      fields: [
        { key: 'propertyAddress', label: 'Address' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'zipCode', label: 'Zip' },
        { key: 'propertyType', label: 'Property type (home style)' },
        { key: 'propertyStatus', label: 'Resale or new construction', type: 'select', options: [['resale', 'Resale'], ['new-construction', 'New construction']] },
      ],
    },
    {
      title: 'Deadlines',
      fields: [
        { key: 'contractDate', label: 'Under contract date', type: 'date' },
        { key: 'closingDate', label: 'Anticipated closing', type: 'date' },
        { key: 'firstEscrowDueDate', label: 'First earnest money due', type: 'date' },
        { key: 'secondEscrowDueDate', label: 'Second earnest money due', type: 'date' },
        { key: 'homeInspectionDeadline', label: 'Home inspection (due diligence)', type: 'date' },
        { key: 'appraisalDeadline', label: 'Appraisal deadline', type: 'date', when: () => !nc },
        { key: 'financingDeadline', label: 'Financing deadline', type: 'date', when: () => financing },
        { key: 'homeSaleContingencyDate', label: 'Home to sell contingency date', type: 'date', when: () => homeSale },
      ],
    },
    {
      title: 'Financials',
      fields: [
        { key: 'offerPrice', label: 'Offer price', type: 'money' },
        { key: 'firstEscrowAmount', label: 'First earnest money amount', type: 'money' },
        { key: 'secondEscrowAmount', label: 'Second earnest money amount', type: 'money' },
        { key: 'paymentType', label: 'Payment', type: 'select', options: [['Cash', 'Cash'], ['Financing', 'Financing']] },
        { key: 'financingSource', label: 'Lender type', type: 'select', options: [['Builder lender', 'Builder lender'], ['Outside lender', 'Outside lender']], when: () => financing && nc },
        { key: 'commissionType', label: 'Commission type', type: 'select', options: [['percentage', 'Percentage'], ['dollar', 'Dollar amount']] },
        { key: 'commissionPercentage', label: 'Commission %', type: 'text', when: () => tx.commissionType !== 'dollar' },
        { key: 'commissionDollarAmount', label: 'Commission amount', type: 'money', when: () => tx.commissionType === 'dollar' },
        ...(nc ? [
          { key: 'basePrice', label: 'Base price', type: 'money' },
          { key: 'builderClosingCostAmount', label: 'Closing costs paid by builder', type: 'money' },
          { key: 'commissionBase', label: 'Commission based on', type: 'select', options: [['Contract Price', 'Contract price'], ['Base Price', 'Base price']] },
          { key: 'reduceBaseByBuilderCC', label: 'Reduce base by builder closing costs?', type: 'select', options: yesNo },
          { key: 'builderBonusType', label: 'Builder bonus', type: 'select', options: [['percentage', 'Percentage'], ['flat', 'Flat amount'], ['none', 'None']] },
          { key: 'builderBonusPercent', label: 'Builder bonus %', type: 'text', when: () => tx.builderBonusType === 'percentage' },
          { key: 'builderBonusAmount', label: 'Builder bonus amount', type: 'money', when: () => tx.builderBonusType === 'flat' },
        ] : []),
      ],
    },
    ...(financing ? [{
      title: 'Lender',
      fields: [
        { key: 'lender.companyName', label: 'Company' },
        { key: 'lender.firstName', label: 'Loan officer first name' },
        { key: 'lender.lastName', label: 'Loan officer last name' },
        { key: 'lender.email', label: 'Email', type: 'email' },
        { key: 'lender.phone', label: 'Phone', type: 'tel' },
      ],
    }] : []),
    nc ? {
      title: 'Builder',
      fields: [
        { key: 'builderName', label: 'Builder' },
        { key: 'communityName', label: 'Community' },
        { key: 'floorplan', label: 'Floorplan' },
        { key: 'builderRepFirstName', label: 'Builder rep first name' },
        { key: 'builderRepLastName', label: 'Builder rep last name' },
        { key: 'builderRepPhone', label: 'Builder rep phone', type: 'tel' },
        { key: 'builderRepEmail', label: 'Builder rep email', type: 'email' },
      ],
    } : {
      title: 'Seller side',
      fields: [
        { key: 'sellerName', label: 'Seller name' },
        { key: 'sellerAgent', label: "Seller's agent" },
        { key: 'sellerAgentPhone', label: "Seller's agent phone", type: 'tel' },
        { key: 'sellerAgentEmail', label: "Seller's agent email", type: 'email' },
        { key: 'sellerAgentTCName', label: "Seller's TC name" },
        { key: 'sellerAgentTCPhone', label: "Seller's TC phone", type: 'tel' },
        { key: 'sellerAgentTCEmail', label: "Seller's TC email", type: 'email' },
      ],
    },
    {
      title: 'Contingencies',
      fields: [
        { key: 'associationApprovalRequired', label: 'Association approval required?', type: 'select', options: yesNo, when: () => !nc },
        { key: 'homeSaleContingency', label: 'Home to sell contingency?', type: 'select', options: yesNo },
        { key: 'homeSaleAgentFirstName', label: "Buyer's listing agent (other home) first name", when: () => homeSale },
        { key: 'homeSaleAgentLastName', label: 'Listing agent last name', when: () => homeSale },
        { key: 'homeSaleAgentEmail', label: 'Listing agent email', type: 'email', when: () => homeSale },
        { key: 'homeSaleAgentPhone', label: 'Listing agent phone', type: 'tel', when: () => homeSale },
      ],
    },
    {
      title: 'Notes',
      fields: [{ key: 'notes', label: 'Notes', type: 'textarea' }],
    },
  ];
};

const shown = (tx, field) => (field.when ? field.when(tx) : true);

const displayValue = (tx, field) => {
  const v = getVal(tx, field.key);
  if (!v && v !== 0) return '';
  if (field.type === 'date') return fmtDate(v);
  if (field.type === 'money') return money(v);
  if (field.type === 'select' && field.options) {
    const hit = field.options.find(([val]) => val === v);
    return hit ? hit[1] : String(v);
  }
  return String(v);
};

const styles = {
  section: { background: '#FFFFFF', border: '1px solid #E1E4EA', borderRadius: 8, padding: '14px 16px', marginBottom: 16 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #D4AF37', paddingBottom: 8, marginBottom: 12 },
  title: { margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a1a' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 },
  label: { fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8C6D1F', fontWeight: 700, display: 'block', marginBottom: 4 },
  value: { fontSize: 15, color: '#1a1a1a', wordBreak: 'break-word' },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1a1a1a', background: '#FFFFFF' },
  gold: { padding: '6px 12px', borderRadius: 6, border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  ghost: { padding: '6px 12px', borderRadius: 6, border: '1px solid #D5D9E0', background: '#FFFFFF', color: '#5B6270', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  error: { color: '#B42323', fontSize: 13, marginTop: 8 },
};

function Section({ section, tx, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fields = section.fields.filter((f) => shown(tx, f));

  const startEdit = () => {
    const d = {};
    section.fields.forEach((f) => { d[f.key] = getVal(tx, f.key) ?? ''; });
    setDraft(d);
    setError('');
    setEditing(true);
  };

  const save = async () => {
    const updates = {};
    section.fields.forEach((f) => {
      const v = draft[f.key] ?? '';
      if (f.key.includes('.')) {
        const [obj, sub] = f.key.split('.');
        updates[obj] = { ...(updates[obj] || tx[obj] || {}), [sub]: v };
      } else {
        updates[f.key] = v;
      }
    });
    try {
      setBusy(true);
      await onSave(updates);
      setEditing(false);
    } catch (err) {
      console.error('Could not save section', err);
      setError('Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const renderInput = (f) => {
    const v = draft[f.key] ?? '';
    const set = (val) => setDraft({ ...draft, [f.key]: val });
    if (f.type === 'select') {
      return (
        <select value={v} onChange={(e) => set(e.target.value)} style={styles.input}>
          <option value="">Choose</option>
          {f.options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
        </select>
      );
    }
    if (f.type === 'textarea') {
      return <textarea value={v} onChange={(e) => set(e.target.value)} rows={3} style={styles.input} />;
    }
    return <input type={f.type === 'date' ? 'date' : f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : 'text'} value={v} onChange={(e) => set(e.target.value)} style={styles.input} />;
  };

  return (
    <div style={styles.section}>
      <div style={styles.head}>
        <h3 style={styles.title}>{section.title}</h3>
        {!editing ? (
          <button type="button" onClick={startEdit} style={styles.ghost}>Edit</button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={save} disabled={busy} style={styles.gold}>{busy ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} style={styles.ghost}>Cancel</button>
          </div>
        )}
      </div>
      {!editing ? (
        <div style={styles.grid}>
          {fields.map((f) => (
            <div key={f.key}>
              <span style={styles.label}>{f.label}</span>
              <div style={styles.value}>{displayValue(tx, f) || <span style={{ color: '#9AA1AD' }}>Not set</span>}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.grid}>
          {section.fields.filter((f) => (f.when ? f.when({ ...tx, ...draft }) : true)).map((f) => (
            <div key={f.key}>
              <span style={styles.label}>{f.label}</span>
              {renderInput(f)}
            </div>
          ))}
        </div>
      )}
      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

export default function BuyerDetailSections({ transaction, onSave }) {
  return (
    <>
      {buildSections(transaction).map((section) => (
        <Section key={section.title} section={section} tx={transaction} onSave={onSave} />
      ))}
    </>
  );
}
